import React from 'react';
import { Brain, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function LLMSelector({ selectedLlms, setSelectedLlms, maxLlms, availableLlms }) {
  const { t } = useLanguage();

  const toggleLlm = (llmId) => {
    if (selectedLlms.includes(llmId)) {
      setSelectedLlms(selectedLlms.filter(id => id !== llmId));
    } else if (selectedLlms.length < maxLlms) {
      setSelectedLlms([...selectedLlms, llmId]);
    }
  };

  const creditCost = selectedLlms.length || 0;

  return (
    <div className="glass rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-emerald-400" />
          <span className="text-white font-medium">{t('llm_engines')}</span>
        </div>
        <span className="text-slate-400 text-sm">
          {selectedLlms.length}/{maxLlms} {t('max')}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {Object.entries(availableLlms).map(([id, info]) => {
          const isSelected = selectedLlms.includes(id);
          const isDisabled = !info.available || (!isSelected && selectedLlms.length >= maxLlms);

          return (
            <button
              key={id}
              onClick={() => !isDisabled && toggleLlm(id)}
              disabled={isDisabled && !isSelected}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                isSelected
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-white'
                  : info.available
                    ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:border-white/20'
                    : 'bg-white/[0.02] border-white/5 text-slate-600 cursor-not-allowed'
              }`}
            >
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                isSelected
                  ? 'bg-emerald-500 border-emerald-500'
                  : 'border-slate-600'
              }`}>
                {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{info.name}</p>
                <p className="text-xs text-slate-500 truncate">{info.description}</p>
              </div>
              <span className="text-xs text-slate-500 flex-shrink-0">
                ${info.cost_per_query}
              </span>
            </button>
          );
        })}
      </div>

      {creditCost > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-sm">
          <span className="text-slate-400">
            {t('llm_cost')}: {creditCost} {creditCost > 1 ? t('credits') : t('credit')}
          </span>
          <span className="text-emerald-400 font-medium">
            {selectedLlms.length} {selectedLlms.length > 1 ? t('llm_engines_selected') : t('llm_engine_selected')}
          </span>
        </div>
      )}
    </div>
  );
}
